import { inject, Injectable, signal } from '@angular/core';

import { BROUTER_MAP_CONFIG } from '../config/brouter-map.config';

export interface MapLayerDefinition {
  /** Stable id, also the key used in persisted state. */
  id: string;
  name: string;
  urlTemplate: string;
  attribution: string;
  maxZoom: number;
  /** Subdomains for `{s}` in the template. */
  subdomains?: string;
}

/** Persisted under the legacy key so existing user preferences survive the migration. */
const STORAGE_KEY = 'map/defaultLayers';

/**
 * Base layers, in the order the legacy layer switcher presented them.
 *
 * `BR.conf.defaultBaseLayerIndex` is 1, so OpenTopoMap is selected on first load.
 * Stamen.Terrain, present in the legacy list, is omitted: Stamen tiles moved to Stadia
 * Maps and now require an API key that this deployment does not hold.
 */
export const BASE_LAYERS: MapLayerDefinition[] = [
  {
    id: 'standard',
    name: 'OpenStreetMap',
    urlTemplate: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19
  },
  {
    id: 'OpenTopoMap',
    name: 'OpenTopoMap',
    urlTemplate: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors, SRTM | &copy; OpenTopoMap (CC-BY-SA)',
    maxZoom: 17,
    subdomains: 'abc'
  },
  {
    id: 'Esri.WorldImagery',
    name: 'Esri World Imagery',
    urlTemplate:
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    maxZoom: 19
  }
];

/** Rail-specific overlays, the reason this deployment uses BRouter at all. */
export const OVERLAY_LAYERS: MapLayerDefinition[] = [
  {
    id: 'openrailwaymap',
    name: 'OpenRailwayMap',
    urlTemplate: 'https://{s}.tiles.openrailwaymap.org/standard/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors | OpenRailwayMap (CC-BY-SA)',
    maxZoom: 19,
    subdomains: 'abc'
  },
  {
    id: 'openrailwaymap_electrification',
    name: 'OpenRailwayMap Electrification',
    urlTemplate: 'https://{s}.tiles.openrailwaymap.org/electrification/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors | OpenRailwayMap (CC-BY-SA)',
    maxZoom: 19,
    subdomains: 'abc'
  }
];

interface PersistedLayers {
  baseLayers: string[];
  overlays: string[];
}

/**
 * Chooses and remembers map layers.
 *
 * Condensed port of `js/LayersConfig.js`, `control/Layers.js` and `control/LayersTab.js`.
 * The legacy client resolved thousands of layers from a bundled index plus the
 * `leaflet-providers` catalogue; this deployment only ever exposed the handful above.
 */
@Injectable({ providedIn: 'root' })
export class LayersConfigService {
  private readonly config = inject(BROUTER_MAP_CONFIG);

  readonly baseLayers = BASE_LAYERS;
  readonly overlays = OVERLAY_LAYERS;

  readonly activeBaseLayerId = signal<string>(this.initialBaseLayerId());
  readonly activeOverlayIds = signal<string[]>(this.initialOverlayIds());

  setActiveBaseLayer(id: string): void {
    this.activeBaseLayerId.set(id);
    this.persist();
  }

  toggleOverlay(id: string): void {
    this.activeOverlayIds.update(current =>
      current.includes(id) ? current.filter(existing => existing !== id) : [...current, id]
    );
    this.persist();
  }

  getBaseLayer(id: string): MapLayerDefinition | undefined {
    return this.baseLayers.find(layer => layer.id === id);
  }

  getOverlay(id: string): MapLayerDefinition | undefined {
    return this.overlays.find(layer => layer.id === id);
  }

  private initialBaseLayerId(): string {
    const stored = this.read()?.baseLayers?.[0];
    if (stored && this.baseLayers.some(layer => layer.id === stored)) {
      return stored;
    }
    const index = Math.min(this.config.defaultBaseLayerIndex, this.baseLayers.length - 1);
    return this.baseLayers[Math.max(0, index)].id;
  }

  private initialOverlayIds(): string[] {
    const stored = this.read()?.overlays;
    if (stored) {
      return stored.filter(id => this.overlays.some(layer => layer.id === id));
    }
    return [];
  }

  private read(): PersistedLayers | null {
    try {
      const item = localStorage.getItem(STORAGE_KEY);
      return item ? (JSON.parse(item) as PersistedLayers) : null;
    } catch {
      // Private browsing modes can make localStorage throw on access.
      return null;
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          baseLayers: [this.activeBaseLayerId()],
          overlays: this.activeOverlayIds()
        } satisfies PersistedLayers)
      );
    } catch {
      // Layer choice is a convenience; failing to persist it must not break the map.
    }
  }
}
